import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AuthenticatedSession {
  accessToken: string | null;
  refreshToken?: string | null;
  sessionId?: string | null;
  user?: any | null;
  permissions?: string[];
  mfaVerified?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  private accessTokenSubject = new BehaviorSubject<string | null>(null);
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private sessionIdSubject = new BehaviorSubject<string | null>(null);
  private userSubject = new BehaviorSubject<any | null>(null);
  private permissionsSubject = new BehaviorSubject<string[]>([]);
  private mfaVerifiedSubject = new BehaviorSubject<boolean>(false);
  private mfaStatusKnown = false;

  accessToken$: Observable<string | null> = this.accessTokenSubject.asObservable();
  refreshToken$: Observable<string | null> = this.refreshTokenSubject.asObservable();
  sessionId$: Observable<string | null> = this.sessionIdSubject.asObservable();
  user$: Observable<any | null> = this.userSubject.asObservable();
  permissions$: Observable<string[]> = this.permissionsSubject.asObservable();
  mfaVerified$: Observable<boolean> = this.mfaVerifiedSubject.asObservable();

  setAuthenticatedSession(session: AuthenticatedSession): void {
    this.accessTokenSubject.next(session.accessToken);
    if (session.refreshToken !== undefined) {
      this.refreshTokenSubject.next(session.refreshToken);
    }
    if (session.sessionId !== undefined) {
      this.sessionIdSubject.next(session.sessionId);
    }
    this.userSubject.next(session.user ?? null);
    this.permissionsSubject.next(session.permissions ?? []);
    if (session.mfaVerified !== undefined) {
      this.setMfaVerified(session.mfaVerified);
    }
  }

  setMfaVerified(verified: boolean): void {
    this.mfaStatusKnown = true;
    this.mfaVerifiedSubject.next(verified);
  }

  setRefreshToken(refreshToken: string | null): void {
    this.refreshTokenSubject.next(refreshToken);
  }

  setSessionId(sessionId: string | null): void {
    this.sessionIdSubject.next(sessionId);
  }

  clearSession(): void {
    this.accessTokenSubject.next(null);
    this.refreshTokenSubject.next(null);
    this.sessionIdSubject.next(null);
    this.userSubject.next(null);
    this.permissionsSubject.next([]);
    this.mfaVerifiedSubject.next(false);
    this.mfaStatusKnown = false;
  }

  getAccessToken(): string | null { return this.accessTokenSubject.value; }
  getRefreshToken(): string | null { return this.refreshTokenSubject.value; }
  getSessionId(): string | null { return this.sessionIdSubject.value; }
  getUser(): any | null { return this.userSubject.value; }
  getPermissions(): string[] { return this.permissionsSubject.value; }
  isMfaVerified(): boolean { return this.mfaVerifiedSubject.value; }
  hasMfaStatus(): boolean { return this.mfaStatusKnown; }
}
